/**
 * Daily Intelligence Report
 * The briefing that greets you on sign-in, as on the live product: three tabs
 * (Overview & Metrics / Insights / Action Items), a confidence pill, refresh,
 * and a rating. Shown once per day per browser.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, ICONS } from '../shell/icons';
import { Tag } from '../ui';
import { INTELLIGENCE_REPORT as R } from '../../demo/live';

const SEEN_KEY = 'omnimind_brief_seen';

/** Has today's briefing already been shown in this browser? */
export function briefingSeenToday(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === new Date().toDateString();
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, new Date().toDateString());
  } catch {
    /* private mode - show it again next time */
  }
}

type Tab = 'overview' | 'insights' | 'actions';

export const IntelligenceReportModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [rating, setRating] = useState(0);
  const [done, setDone] = useState<number[]>([]);

  const close = () => {
    markSeen();
    onClose();
  };

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview & metrics' },
    { id: 'insights', label: 'Insights', count: R.positives.length + R.attention.length },
    { id: 'actions', label: 'Action items', count: R.actions.length },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 95, display: 'grid', placeItems: 'center', padding: 16 }}
      role="dialog"
      aria-modal="true"
    >
      <div className="ovl" style={{ zIndex: 0 }} onClick={close} aria-hidden="true" />

      <div
        className="sheet animate-scale-in"
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 880, maxHeight: '88vh' }}
      >
        {/* header */}
        <div
          style={{
            background: 'conic-gradient(from 200deg,#2354F0,#6D5AE6,#D97757,#12B981,#2354F0)',
            padding: '18px 22px',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span
            style={{
              width: 42,
              height: 42,
              borderRadius: 13,
              background: 'rgba(255,255,255,.2)',
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
            }}
          >
            <Icon d={ICONS.chart} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>Good morning, John</div>
            <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.035em' }}>{R.title}</h3>
            <div style={{ fontSize: 11.5, opacity: 0.85 }}>{today}</div>
          </div>
          <span
            className="chip2"
            style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff' }}
          >
            <i style={{ background: R.confidence >= 80 ? '#7FE9C4' : '#F5C26B' }} />
            {R.confidence}% confidence
          </span>
          <button
            className="icb"
            style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff' }}
            title="Regenerate"
          >
            <Icon d={ICONS.sync} />
          </button>
          <button
            className="icb"
            style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff' }}
            onClick={close}
            aria-label="Close"
          >
            <Icon d={ICONS.x} />
          </button>
        </div>

        {/* tabs */}
        <div style={{ padding: '12px 20px 0', borderBottom: '1px solid var(--line)' }}>
          <div className="tabs" style={{ marginBottom: 12 }}>
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`tab${tab === t.id ? ' on' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.count !== undefined && ` (${t.count})`}
              </button>
            ))}
          </div>
        </div>

        {/* body */}
        <div className="sheet-b custom-scrollbar">
          <div className="note" style={{ display: 'flex', gap: 11, marginBottom: 14 }}>
            <span
              className="kic"
              style={{ background: 'var(--purple-s)', color: 'var(--purple)', width: 30, height: 30, flex: 'none' }}
            >
              <Icon d={ICONS.file} />
            </span>
            <div>
              <div style={{ fontWeight: 800, color: 'var(--ink)', fontSize: 12.5 }}>Executive summary</div>
              <div style={{ marginTop: 2 }}>{R.summary}</div>
            </div>
          </div>

          {tab === 'overview' && (
            <>
              <div className="sec-t" style={{ marginTop: 0 }}>
                Key performance metrics
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 9,
                }}
              >
                {R.metrics.map((m) => (
                  <div className="ak" key={m.label}>
                    <div className="l">{m.label}</div>
                    <div className="v">{m.value}</div>
                    <div className="d dim">{m.context}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'insights' && (
            <>
              <div className="sec-t" style={{ marginTop: 4 }}>
                Positive developments ({R.positives.length})
              </div>
              {R.positives.map((p) => (
                <div
                  key={p.title}
                  style={{
                    borderLeft: '3px solid var(--green)',
                    background: 'var(--green-s)',
                    borderRadius: 12,
                    padding: '11px 14px',
                    marginBottom: 9,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 13 }}>{p.title}</b>
                    <Tag tone="g">{p.severity}</Tag>
                    <Tag>{p.category}</Tag>
                  </div>
                  <p style={{ fontSize: 12, color: '#2A3338', marginTop: 5, lineHeight: 1.5 }}>{p.body}</p>
                </div>
              ))}

              <div className="sec-t">Areas requiring attention ({R.attention.length})</div>
              {R.attention.map((a) => (
                <div
                  key={a.title}
                  style={{
                    borderLeft: `3px solid ${a.severity === 'HIGH' ? 'var(--amber)' : 'var(--blue)'}`,
                    background: a.severity === 'HIGH' ? 'var(--amber-s)' : 'var(--blue-s)',
                    borderRadius: 12,
                    padding: '11px 14px',
                    marginBottom: 9,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 13 }}>{a.title}</b>
                    <Tag tone={a.severity === 'HIGH' ? 'a' : 'b'}>{a.severity}</Tag>
                    <Tag>{a.category}</Tag>
                  </div>
                  <p style={{ fontSize: 12, color: '#2A3338', marginTop: 5, lineHeight: 1.5 }}>{a.body}</p>
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: 10,
                      padding: '8px 11px',
                      marginTop: 8,
                      display: 'flex',
                      gap: 9,
                    }}
                  >
                    <span className="eye" style={{ margin: 0, color: 'var(--amber)', flex: 'none' }}>
                      Recommended
                    </span>
                    <span style={{ fontSize: 12 }}>{a.action}</span>
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === 'actions' && (
            <>
              <div className="sec-t" style={{ marginTop: 4 }}>
                Recommended actions
              </div>
              {R.actions.map((a) => {
                const ticked = done.includes(a.n);
                return (
                  <div
                    key={a.n}
                    className="card"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      marginBottom: 9,
                      borderLeft: `3px solid ${a.tone === 'a' ? 'var(--amber)' : 'var(--blue)'}`,
                      opacity: ticked ? 0.55 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 9,
                        background: a.tone === 'a' ? 'var(--amber)' : 'var(--blue)',
                        color: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 800,
                        fontSize: 12,
                        flex: 'none',
                      }}
                    >
                      {a.n}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          textDecoration: ticked ? 'line-through' : 'none',
                        }}
                      >
                        {a.text}
                      </div>
                      <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
                        <Tag tone={a.tone as any}>{a.priority}</Tag>
                        <Tag>{a.category}</Tag>
                      </div>
                    </div>
                    <button
                      className="icb"
                      title={ticked ? 'Mark as not done' : 'Mark as done'}
                      onClick={() =>
                        setDone(ticked ? done.filter((n) => n !== a.n) : [...done, a.n])
                      }
                      style={ticked ? { background: 'var(--green-s)', color: '#0A7A55' } : undefined}
                    >
                      <Icon d={ICONS.check} strokeWidth={2.4} />
                    </button>
                  </div>
                );
              })}
              <div className="cmeta" style={{ marginTop: 10 }}>
                <Tag tone="r">0 urgent</Tag>
                <Tag tone="a">1 important</Tag>
                <Tag tone="b">1 normal</Tag>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {R.actions.length} total actions
                </span>
              </div>
            </>
          )}
        </div>

        {/* footer */}
        <div className="sheet-f" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="dim" style={{ fontSize: 11.5, fontWeight: 700 }}>
            Rate this report
          </span>
          <div style={{ display: 'flex', gap: 3 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n)}
                title={`${n} of 5`}
                style={{
                  color: n <= rating ? 'var(--amber)' : 'var(--muted-2)',
                  fontSize: 15,
                  lineHeight: 1,
                }}
              >
                ★
              </button>
            ))}
          </div>
          <span className="dim" style={{ fontSize: 11.5 }}>
            {R.categoriesAnalysed} categories analysed
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              className="btn btn-g"
              onClick={() => {
                markSeen();
                onClose();
                navigate('/daily-report');
              }}
            >
              Open full report
            </button>
            <button className="btn btn-p" onClick={close}>
              Got it, thanks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
